import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { navigationService } from '@wf/sw-ui-kit';
import { type IError } from '@wf/sw-ui-kit';

import * as licenseTypeService from '../../../services/licenseTypeApiService';
import * as configsService from '../../../services/configsService';

import { type ILicenseTypeConfigResponse, type ILpcoFormDetails, type ILpcoResponse } from '../types/response';
import { type IValidationConfigResponse, type TValidationConfig } from '../types/configs';
import { type IErrorResponse, type LicenseTypeNature } from '../../../types';
import { OperationType, Status } from '../types/enums';

import {
  setConfiguration,
  setErrors,
  setLicenseType,
  setLicenseTypeLoading,
  setLpco,
  setLpcoIsFetched,
  setLpcoLoading,
  setLpcoSubmitting,
  setMandatoryAttachmentCodes,
  setParentLicenseType,
  setParentLicenseTypeId,
  setParentLpco,
  setParentLpcoLoading,
  setSelectedLicenseTypeId,
  setUserReferenceNumber,
  setValidationConfig,
} from './slice';
import {
  additionalFieldsMapByTabSelector,
  editableFieldsByOperationSelector,
  licenseTypeByIdSelector,
  selectedLicenseTypeNatureSelector,
} from './selectors';
import {
  convertFilexDataToFormData,
  type IAttachedDocumentExtractionResponse,
} from '../helpers/convertFilexDataToFormData';
import { convertFieldDataForBackEnd } from '../helpers/convertFieldDataForBackEnd';
import convertValidationConfig from '../helpers/convertValidationConfig';
import convertErrors from '../helpers/convertErrors';
import { toasterSuccess } from '../../../helpers';
import * as filexService from '../services/filex';
import * as lpcoService from '../services/lpco';
import * as actions from './actions';

function* getLpcoFormDetails({ payload }: ReturnType<typeof actions.getLpcoFormDetails>) {
  try {
    yield put(setLpcoLoading(true));
    const {
      data: { lpcoResponse, configs },
    }: { data: ILpcoFormDetails } = yield call(lpcoService.getLpcoFormDetails, payload);

    yield put(setLpco(lpcoResponse));
    yield put(setConfiguration(configs));
    const { status, licenseTypeId, licenseType, parentLpcoId } = lpcoResponse;

    if (status === Status.STORED) {
      yield put(actions.getLicenseTypeByCode({ licenseTypeCode: licenseType, id: licenseTypeId as number }));
    } else {
      yield put(actions.getLicenseTypeById(licenseTypeId as number));
    }

    if (parentLpcoId) {
      yield put(actions.getParentLpco(parentLpcoId));
    }
  } catch (e) {
    console.error(e);
  } finally {
    yield put(setLpcoLoading(false));
    yield put(setLpcoIsFetched(true));
  }
}

function* getFilexData({ payload }: ReturnType<typeof actions.getFilexData>) {
  try {
    yield put(setLpcoLoading(true));
    const { data }: { data: IAttachedDocumentExtractionResponse } = yield call(filexService.getFilexData, payload);

    const filexData: ILpcoResponse = convertFilexDataToFormData(data);
    const { licenseType } = filexData;

    const { data: licenseTypeConfig } = yield call(licenseTypeService.getLicenseTypeByCode, licenseType);
    yield put(setLicenseType(licenseTypeConfig as ILicenseTypeConfigResponse));

    yield put(setConfiguration((licenseTypeConfig as ILicenseTypeConfigResponse).configs));
    yield put(setLpco(filexData));
  } catch (e) {
    console.error(e);
  } finally {
    yield put(setLpcoLoading(false));
  }
}

function* getParentLpco({ payload }: ReturnType<typeof actions.getParentLpco>) {
  try {
    yield put(setParentLpcoLoading(true));
    const {
      data: { lpcoResponse },
    }: { data: ILpcoFormDetails } = yield call(lpcoService.getLpcoFormDetails, payload);

    yield put(setParentLpco(lpcoResponse));

    const { licenseTypeId } = lpcoResponse;
    yield put(actions.getParentLicenseTypeById(licenseTypeId as number));
  } catch (e) {
    console.error(e);
  } finally {
    yield put(setParentLpcoLoading(false));
  }
}

function* getLicenseTypeById({ payload }: ReturnType<typeof actions.getLicenseTypeById>) {
  const selectedLicenseType: ILicenseTypeConfigResponse | undefined = yield select(licenseTypeByIdSelector(payload));

  if (selectedLicenseType) {
    yield put(setSelectedLicenseTypeId(payload));

    return;
  }

  try {
    yield put(setLicenseTypeLoading(true));

    const { data } = yield call(licenseTypeService.getLicenseTypeById, payload);
    yield put(setLicenseType(data as ILicenseTypeConfigResponse));
  } catch (e) {
    console.error(e);
  } finally {
    yield put(setLicenseTypeLoading(false));
  }
}

function* getParentLicenseTypeById({ payload }: ReturnType<typeof actions.getParentLicenseTypeById>) {
  const parentLicenseType: ILicenseTypeConfigResponse | undefined = yield select(licenseTypeByIdSelector(payload));

  if (parentLicenseType) {
    yield put(setParentLicenseTypeId(payload));

    return;
  }

  try {
    yield put(setLicenseTypeLoading(true));

    const { data } = yield call(licenseTypeService.getLicenseTypeById, payload);
    yield put(setParentLicenseType(data as ILicenseTypeConfigResponse));
  } catch (e) {
    console.error(e);
  } finally {
    yield put(setLicenseTypeLoading(false));
  }
}

function* getLicenseTypeByCode({
  payload: { licenseTypeCode, id, generateConfiguration },
}: ReturnType<typeof actions.getLicenseTypeByCode>) {
  if (typeof id === 'number') {
    const selectedLicenseType: ILicenseTypeConfigResponse | undefined = yield select(licenseTypeByIdSelector(id));

    if (selectedLicenseType) {
      yield put(setSelectedLicenseTypeId(id));

      if (generateConfiguration) {
        yield put(setConfiguration(selectedLicenseType.configs));
      }

      return;
    }
  }

  try {
    yield put(setLicenseTypeLoading(true));

    const { data } = yield call(licenseTypeService.getLicenseTypeByCode, licenseTypeCode);
    yield put(setLicenseType(data as ILicenseTypeConfigResponse));

    if (generateConfiguration) {
      yield put(setConfiguration((data as ILicenseTypeConfigResponse).configs));
    }
  } catch (e) {
    console.error(e);
  } finally {
    yield put(setLicenseTypeLoading(false));
  }
}

function* getUserReferenceNumber() {
  try {
    const { data } = yield call(lpcoService.getUserReferenceNumber);
    yield put(setUserReferenceNumber(data as string));
  } catch (e) {
    console.error(e);
  }
}

function* lpcoOperation({ payload }: ReturnType<typeof actions.lpcoOperation>) {
  try {
    yield put(setLpcoSubmitting(true));
    const operation = payload.selectedOperation as OperationType;
    const editableFields: (OperationType | string)[] = yield select(editableFieldsByOperationSelector(operation));

    const isPredefinedOperation = !!OperationType[operation];

    const convertedData = convertFieldDataForBackEnd(payload, isPredefinedOperation, editableFields);
    yield call(lpcoService.lpcoOperationRequest, {
      id: payload.id,
      operation: isPredefinedOperation ? operation : OperationType.OGA_UPDATE,
      data: convertedData,
    });
    toasterSuccess({ title: 'The request is done successfully' });

    let navigateUrl = '../..';
    if (payload.id && payload.isCreatingLicenseFromFilex) navigateUrl = '../../..';
    if (!payload.id && !payload.isCreatingLicenseFromFilex) navigateUrl = '..';

    navigationService.navigate(navigateUrl, { relative: 'path' });
  } catch (error) {
    if (Array.isArray(error)) {
      const additionalFieldsByTab: ReturnType<typeof additionalFieldsMapByTabSelector> = yield select(
        additionalFieldsMapByTabSelector
      );
      const licenseTypeNature: LicenseTypeNature = yield select(selectedLicenseTypeNatureSelector);
      const { convertedErrors, mandatoryAttachments } = convertErrors(
        error as IErrorResponse[],
        additionalFieldsByTab,
        licenseTypeNature
      );
      yield put(setErrors(convertedErrors as IError[]));
      yield put(setMandatoryAttachmentCodes(mandatoryAttachments));
    }
  } finally {
    yield put(setLpcoSubmitting(false));
  }
}

function* getValidationConfig() {
  try {
    const { data } = yield call(configsService.getValidationConfig);
    const convertedData: TValidationConfig = convertValidationConfig(data as IValidationConfigResponse);
    yield put(setValidationConfig(convertedData));
  } catch (e) {
    console.error(e);
  }
}

export default function* watchLicenseSaga() {
  yield takeLatest(actions.lpcoOperation, lpcoOperation);
  yield takeLatest(actions.getLpcoFormDetails, getLpcoFormDetails);
  yield takeLatest(actions.getFilexData, getFilexData);
  yield takeEvery(actions.getLicenseTypeById, getLicenseTypeById);
  yield takeLatest(actions.getParentLicenseTypeById, getParentLicenseTypeById);
  yield takeLatest(actions.getLicenseTypeByCode, getLicenseTypeByCode);
  yield takeLatest(actions.getUserReferenceNumber, getUserReferenceNumber);
  yield takeLatest(actions.getValidationConfig, getValidationConfig);
  yield takeLatest(actions.getParentLpco, getParentLpco);
}
